import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import FormInput from '../../../shared/components/ui/FormInput';
import Multiselect from 'multiselect-react-dropdown';
import Button from '../../../shared/components/ui/Button';
import { getAllAccommodationsByProject } from '../../services/api/implementation/impl';
import {
  getCountryCodeOptions,
  validatePhoneNumber,
  getPhoneNumberLength,
  cleanPhoneNumber,
  getDefaultCountryCode,
  detectCountryFromPhoneNumber,
  getCountryCallingCode
} from '../../utils/phoneNumberUtils';
import { capitalizeWords } from '../../utils/stringUtils';

const editLeadSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  countryCode: yup.string().required('Country code is required'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .test('phone-validation', 'Please enter a valid phone number', function (value) {
      const { countryCode } = this.parent;
      if (!value || !countryCode) return false;
      return validatePhoneNumber(value, countryCode);
    }),
  interested_projects: yup.string().required('Select atleat one project'),
  accomodations: yup.array(),
  plotArea: yup.array(),
  project_accommodations: yup.array(),
  reference_name: yup.string(),
  source: yup.string(),
  expectedBudgetRange: yup.string(),
  expected_clouser_date: yup.string(),
  stage: yup.string(),
  pstatus: yup.string(),
  pincode: yup.string(),
  city: yup.string(),
  state: yup.string()
});

const EditLeadForm = ({ onSubmit, onCancel, isSubmitting, AllProjects, editingLead }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(editLeadSchema),
    defaultValues: {
      accomodations: [],
      plotArea: [],
      project_accommodations: []
    }
  });

  console.log('editingLead', editingLead);

  const editPID = watch('interested_projects');
  const [editProjectAccommodationsKey, setEditProjectAccommodationsKey] = useState(0);

  // Get accommodations for the selected project
  const { data: editAccommodations } = useQuery({
    queryKey: ['EditAccommodations', editPID],
    queryFn: () => getAllAccommodationsByProject(editPID),
    enabled: !!editPID
  });

  // Prefill form when editingLead changes
  useEffect(() => {
    if (editingLead) {
      console.log('LEAD Data:', JSON.stringify(editingLead, null, 2));

      // Basic user info (disabled fields)
      setValue('name', editingLead?.user?.name || '');

      // Handle country code - convert from calling code format to country code format
      let countryCode = editingLead?.user?.countryCode || '+91';
      console.log(
        '🔍 EditLeadForm - Original country code from API:',
        editingLead?.user?.countryCode
      );
      console.log('🔍 EditLeadForm - Available country options:', countryCodeOptions);

      if (countryCode.startsWith('+')) {
        // If it's already in calling code format (+91), find the corresponding country code
        const countryOption = countryCodeOptions.find(
          (option) => option.displayValue === countryCode
        );
        console.log('🔍 EditLeadForm - Found country option:', countryOption);
        countryCode = countryOption ? countryOption.value : getDefaultCountryCode();
      }
      console.log('🔍 EditLeadForm - Final country code to set:', countryCode);
      setValue('countryCode', countryCode);

      setValue('phone_number', editingLead?.user?.phone_number || '');

      // Address fields
      setValue('pincode', editingLead?.address?.zipCode || '');
      setValue('city', editingLead?.address?.city || '');
      setValue('state', editingLead?.address?.state || '');

      // Budget and status
      setValue('expectedBudgetRange', editingLead?.expectedBudgetRange || '');
      setValue('pstatus', editingLead?.pstatus || '');

      // Project - handle both array and single value formats
      if (editingLead?.projects?.[0]?.id) {
        // If projects is an array of objects with id
        setValue('interested_projects', editingLead.projects[0].id.toString());
      } else if (editingLead?.interested_projects?.[0]) {
        // If interested_projects is an array of IDs
        setValue('interested_projects', editingLead.interested_projects[0].toString());
      } else if (editingLead?.interested_projects) {
        // If interested_projects is a single value
        setValue('interested_projects', editingLead.interested_projects.toString());
      }

      // Other fields
      setValue('reference_name', editingLead?.reference_name || '');
      setValue('source', editingLead?.source || '');

      // Stage - get from activities if available
      if (editingLead?.activities?.[0]?.name) {
        setValue('stage', editingLead.activities[0].name);
      } else {
        setValue('stage', editingLead?.stage || '');
      }

      // Expected closure date
      const closureDateStr = editingLead?.expected_clouser_date;

      if (closureDateStr) {
        const parsedDate = new Date(closureDateStr);
        if (!isNaN(parsedDate.getTime())) {
          // Format date as YYYY-MM-DD for HTML date input
          const year = parsedDate.getFullYear();
          const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
          const day = String(parsedDate.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;
          setValue('expected_clouser_date', formattedDate);
        }
      }

      // Set accommodations
      if (editingLead?.interestedAccomodationType) {
        const accommodationOptions = [
          { name: '1BHK', id: '1BHK' },
          { name: '1.5BHK', id: '1.5BHK' },
          { name: '2BHK', id: '2BHK' },
          { name: '2.5BHK', id: '2.5BHK' },
          { name: '3BHK', id: '3BHK' },
          { name: '3.5BHK', id: '3.5BHK' },
          { name: '4BHK', id: '4BHK' },
          { name: '4.5BHK', id: '4.5BHK' },
          { name: '5BHK', id: '5BHK' },
          { name: '5.5BHK', id: '5.5BHK' },
          { name: 'Other', id: 'Other' }
        ];
        const selectedAccommodations = accommodationOptions.filter((option) =>
          editingLead.interestedAccomodationType.includes(option.id)
        );
        setValue('accomodations', selectedAccommodations);
      }

      // Set plot area
      if (editingLead?.plotArea) {
        const selectedPlotAreas = plotAreaOptions.filter((option) =>
          editingLead.plotArea.includes(option.id)
        );
        setValue('plotArea', selectedPlotAreas);
      }

      // Set project accommodations
      if (editingLead?.accomodations && editingLead?.accomodation) {
        const selectedAccommodations = editingLead.accomodations.map((accId) => {
          const acc = editingLead.accomodation.find((a) => a.id === accId);
          return acc || { id: accId, name: `Accommodation ${accId}` };
        });
        setValue('project_accommodations', selectedAccommodations);
      }
    }
  }, [editingLead, setValue]);

  const handleEditProjectChange = useCallback(
    (value) => {
      setValue('interested_projects', value);
      setValue('project_accommodations', []);
      // Force re-render of project accommodations multiselect
      setEditProjectAccommodationsKey((prev) => prev + 1);
    },
    [setValue]
  );

  const handlePincodeChange = useCallback(
    async (value) => {
      const cleanedValue = value?.replace(/[^0-9]/g, '')?.slice(0, 6) || '';
      setValue('pincode', cleanedValue);

      if (cleanedValue.length === 6) {
        try {
          const response = await fetch(`https://api.postalpincode.in/pincode/${cleanedValue}`);
          const data = await response.json();

          if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.[0]) {
            const postOffice = data[0].PostOffice[0];
            setValue('city', postOffice.District || '');
            setValue('state', postOffice.State || '');
          }
        } catch (error) {
          console.error('Error fetching pincode data:', error);
        }
      }
    },
    [setValue]
  );

  const transformedData =
    AllProjects?.map((item) => ({
      label: item?.name?.trim() || '',
      id: item?.id,
      value: item?.id
    })) || [];

  // Use dynamic country code options from the utility
  const countryCodeOptions = getCountryCodeOptions().map((country) => ({
    label: country.label,
    id: country.value,
    value: country.value,
    displayValue: country.displayValue // This will be shown in the input field
  }));

  const handleFormSubmit = (data) => {
    // Construct payload based on the new form structure
    const payload = {
      interested_projects: [Number(data?.interested_projects)],
      name: data.name
    };

    // Log the payload for debugging
    console.log('📤 EditLeadForm - Payload being sent:', payload);

    // Add optional editable fields only if they have values
    if (data.expectedBudgetRange) payload.expectedBudgetRange = data.expectedBudgetRange;
    if (data.reference_name) payload.reference_name = data.reference_name;
    if (data.source) payload.source = data.source;
    if (data.expected_clouser_date) payload.expected_clouser_date = data.expected_clouser_date;
    if (data.pincode) payload.pincode = data.pincode;
    if (data.city) payload.city = data.city;
    if (data.state) payload.state = data.state;
    if (data.accomodations)
      payload.interestedAccomodationType =
        data?.accomodations?.map((item) => item.id || item) || [];
    if (data.plotArea) payload.plotArea = data?.plotArea?.map((item) => item.id || item) || [];
    if (data.project_accommodations)
      payload.accomodations = data?.project_accommodations?.map((item) => item.id || item) || [];

    onSubmit(payload);
  };

  const getSelectedProject = () => {
    if (!editPID) return null;
    return AllProjects?.find((project) => project.id === Number(editPID));
  };
  const selectedProject = getSelectedProject();

  const plotAreaOptions = [
    {
      name: `0-100 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '0-100',
      value: '0-100'
    },
    {
      name: `100-200 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '100-200',
      value: '100-200'
    },
    {
      name: `200-300 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '200-300',
      value: '200-300'
    },
    {
      name: `300 - 500 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '300-500',
      value: '300-500'
    },
    {
      name: `500 - 750 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '500-750',
      value: '500-750'
    },
    {
      name: `750 - 900 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '750-900',
      value: '750-900'
    },
    {
      name: `900 - 1100 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '900-1100',
      value: '900-1100'
    },
    {
      name: `1100 - 1350 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '1100-1350',
      value: '1100-1350'
    },
    {
      name: `1350 - 1600 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '1350-1600',
      value: '1350-1600'
    },
    {
      name: `1600 - 2000 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '1600-2000',
      value: '1600-2000'
    },
    {
      name: `2000 - 2400 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '2000-2400',
      value: '2000-2400'
    },
    {
      name: `2400 - 3000 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '2400-3000',
      value: '2400-3000'
    },
    {
      name: `3000 - 3500 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '3000-3500',
      value: '3000-3500'
    },
    {
      name: `3500 - 4200 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '3500-4200',
      value: '3500-4200'
    },
    {
      name: `4200 - 5000 ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '4200-5000',
      value: '4200-5000'
    },
    {
      name: `5000 - Above ${
        selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''
      }`,
      id: '5000-above',
      value: '5000-above'
    }
  ];

  return (
    <div className="flex flex-col justify-between items-start gap-4">
      <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Edit Lead Details</span>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4 pt-6 w-full">
        <div className="grid grid-cols-2 gap-4">
          {/* Lead Name - Disabled */}
          <FormInput
            width={100}
            label={'Lead Name'}
            register={register}
            register_key={'name'}
            type={'text'}
            errors={errors?.name}
            isRequired={true}
            readOnly={false}
          />

          {/* Mobile Number - Disabled */}
          <div className="w-[100%] grid grid-cols-[140px_1fr] gap-2 items-end">
            <FormInput
              width={100}
              label={'Mobile Number'}
              register={register}
              register_key={'countryCode'}
              type={'CountyCodedropdown'}
              isRequired={true}
              options={countryCodeOptions}
              errors={errors?.countryCode}
              readOnly={true}
              valuePre={watch('countryCode')}
              defaultValuePre={getDefaultCountryCode()}
            />
            <FormInput
              width={100}
              label={''}
              register={register}
              register_key={'phone_number'}
              type={'text'}
              errors={errors?.phone_number}
              isRequired={true}
              hideAstrik={true}
              readOnly={true}
            />
          </div>

          {/* Interested Projects - Editable */}
          <div className="w-[100%]">
            <FormInput
              width={100}
              label={'Interested Projects'}
              register={register}
              register_key={'interested_projects'}
              defaultValuePre={''}
              type={'dropdown'}
              isRequired={true}
              options={[
                { label: 'Select Project', id: '', value: '', selected: true, disabled: true },
                ...transformedData
              ]}
              errors={errors?.interested_projects}
              onDropdownChange={handleEditProjectChange}
            />
          </div>

          {/* Project Accommodations - Editable */}
          <div className="w-[100%] flex flex-col gap-1">
            <label className="text-gray-600 mb-1 block">Project Accommodations</label>
            <Controller
              name="project_accommodations"
              control={control}
              render={({ field }) => (
                <Multiselect
                  key={editProjectAccommodationsKey}
                  options={editAccommodations || []}
                  selectedValues={field.value}
                  onSelect={(selectedList) => field.onChange(selectedList)}
                  onRemove={(selectedList) => field.onChange(selectedList)}
                  displayValue="name"
                  placeholder={editPID ? 'Select project accommodations' : 'Select a project first'}
                  disabled={!editPID}
                  showCheckbox
                />
              )}
            />
          </div>

          {/* Interested Accommodation - Editable */}
          <div className="w-[100%] flex flex-col gap-1">
            <label className="text-gray-600 mb-1 block">Interested Accommodation</label>
            <Controller
              name="accomodations"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={[
                    { name: '1BHK', id: '1BHK', value: '1BHK' },
                    { name: '1.5BHK', id: '1.5BHK', value: '1.5BHK' },
                    { name: '2BHK', id: '2BHK', value: '2BHK' },
                    { name: '2.5BHK', id: '2.5BHK', value: '2.5BHK' },
                    { name: '3BHK', id: '3BHK', value: '3BHK' },
                    { name: '3.5BHK', id: '3.5BHK', value: '3.5BHK' },
                    { name: '4BHK', id: '4BHK', value: '4BHK' },
                    { name: '4.5BHK', id: '4.5BHK', value: '4.5BHK' },
                    { name: '5BHK', id: '5BHK', value: '5BHK' },
                    { name: '5.5BHK', id: '5.5BHK', value: '5.5BHK' },
                    { name: 'Other', id: 'Other', value: 'Other' }
                  ]}
                  selectedValues={field.value}
                  onSelect={(selectedList) => field.onChange(selectedList)}
                  onRemove={(selectedList) => field.onChange(selectedList)}
                  displayValue="name"
                  placeholder="Select Accommodations"
                  showCheckbox
                />
              )}
            />
          </div>

          {/* Plot Area - Editable */}
          <div className="w-[100%] flex flex-col gap-1">
            <label className="text-gray-600 mb-1 block">
              Interested Plot Area{' '}
              {selectedProject?.measurement_type ? `(${selectedProject?.measurement_type})` : ''}
            </label>
            <Controller
              name="plotArea"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={plotAreaOptions}
                  selectedValues={field.value}
                  onSelect={(selectedList) => field.onChange(selectedList)}
                  onRemove={(selectedList) => field.onChange(selectedList)}
                  displayValue="name"
                  placeholder="Select Plot Area"
                  showCheckbox
                />
              )}
            />
          </div>

          {/* Expected Budget - Editable */}
          <FormInput
            width={100}
            label={'Expected Budget'}
            register={register}
            register_key={'expectedBudgetRange'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Budget', disabled: true, selected: true },
              { value: '0-10', label: '0 - 10 Lacs' },
              { value: '10-20', label: '10 Lacs - 20 Lacs' },
              { value: '20-30', label: '20 Lacs - 30 Lacs' },
              { value: '30-40', label: '30 Lacs - 40 Lacs' },
              { value: '40-50', label: '40 Lacs - 50 Lacs' },
              { value: '50-60', label: '50 Lacs - 60 Lacs' },
              { value: '60-75', label: '60 Lacs - 75 Lacs' },
              { value: '75-100', label: '75 Lacs - 100 Lacs' },
              { value: '100-150', label: '100 Lacs - 150 Lacs' },
              { value: '150-200', label: '150 Lacs - 200 Lacs' },
              { value: '200-300', label: '200 Lacs - 300 Lacs' },
              { value: '300-400', label: '300 Lacs - 400 Lacs' },
              { value: '400-above', label: '400 Lacs - Above' }
            ]}
            errors={errors?.expectedBudgetRange}
            isRequired={false}
          />

          {/* Reference Name - Editable */}
          <FormInput
            width={100}
            label={'Nick Name'}
            register={register}
            register_key={'reference_name'}
            type={'text'}
            errors={errors?.reference_name}
            isRequired={false}
            onType={(text) => setValue('reference_name', capitalizeWords(text))}
          />

          {/* Lead Source - Editable */}
          <FormInput
            width={100}
            label={'Lead Source'}
            register={register}
            register_key={'source'}
            defaultValuePre={''}
            type={'dropdown'}
            isRequired={false}
            options={[
              { value: '', label: 'Select Source', disabled: true, selected: true },
              { value: 'Real Estate Brokers/Agents', label: 'Real Estate Brokers/Agents' },
              { value: 'Channel Partners', label: 'Channel Partners' },
              { value: 'Property Listing Portals', label: 'Property Listing Portals' },
              { value: 'Google Ads', label: 'Google Ads' },
              { value: 'Facebook', label: 'Facebook' },
              { value: 'Instagram', label: 'Instagram' },
              { value: 'Company Website', label: 'Company Website' },
              {
                value: 'Walk-ins from Project Signage/Hoardings',
                label: 'Walk-ins from Project Signage/Hoardings'
              },
              {
                value: 'Existing Customer Referrals (Word of Mouth)',
                label: 'Existing Customer Referrals (Word of Mouth)'
              },
              { value: 'Friends/Family', label: 'Friends/Family' },
              { value: 'Tele-calling Campaigns', label: 'Tele-calling Campaigns' },
              { value: 'Email or WhatsApp Campaigns', label: 'Email or WhatsApp Campaigns' },
              { value: 'Offline Events & Exhibitions', label: 'Offline Events & Exhibitions' }
            ]}
            errors={errors?.source}
          />

          {/* Lead Stage - Disabled */}
          <FormInput
            width={100}
            label={'Lead Stage'}
            register={register}
            register_key={'stage'}
            type={'text'}
            errors={errors?.stage}
            isRequired={false}
            readOnly={true}
          />

          {/* Lead Status - Disabled */}
          <FormInput
            width={100}
            label={'Lead Status'}
            register={register}
            register_key={'pstatus'}
            type={'text'}
            errors={errors?.pstatus}
            isRequired={false}
            readOnly={true}
          />

          {/* Expected Closure Date - Editable */}
          <FormInput
            width={100}
            register={register}
            register_key={'expected_clouser_date'}
            label={'Expected Closure Date'}
            type={'date'}
            isRequired={false}
          />

          {/* Pincode - Editable (with API call) */}
          <FormInput
            width={100}
            label={'Pincode'}
            register={register}
            register_key={'pincode'}
            type={'text'}
            errors={errors?.pincode}
            isRequired={false}
            onType={(value) => handlePincodeChange(value)}
          />

          {/* City - Disabled (auto-filled from pincode) */}
          <FormInput
            width={100}
            label={'City'}
            register={register}
            register_key={'city'}
            type={'text'}
            errors={errors?.city}
            isRequired={false}
            readOnly={true}
          />

          {/* State - Disabled (auto-filled from pincode) */}
          <FormInput
            width={100}
            label={'State'}
            register={register}
            register_key={'state'}
            type={'text'}
            errors={errors?.state}
            isRequired={false}
            readOnly={true}
          />
        </div>
        <div className="flex justify-end items-center space-x-4 mt-4">
          <Button type="button" onClick={onCancel} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            Update Lead
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditLeadForm;
